import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import TeamHelmet from '../components/TeamHelmet'
import api from '../services/api'
import {
  normalizePlayableTeamAbbreviation,
  PLAYABLE_TEAM_ABBREVIATIONS,
  type PlayableTeamAbbreviation,
} from '../constants/teamAssets'
import './TeamMatchupMatrix.css'

interface GameSummary {
  id: number
  homeTeam: string
  awayTeam: string
}

type MatrixCounts = Record<PlayableTeamAbbreviation, Record<PlayableTeamAbbreviation, number>>

/**
 * Shows how often each playable team matchup has occurred.
 */
export default function TeamMatchupMatrix() {
  const navigate = useNavigate()
  const tableWrapRef = useRef<HTMLDivElement | null>(null)
  const [games, setGames] = useState<GameSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadGames = async () => {
      try {
        setLoading(true)
        const response = await api.get<GameSummary[]>('/games')
        setGames(response.data ?? [])
        setError(null)
      } catch (err: any) {
        console.error('Failed to load team matchup matrix:', err)
        setError(err.response?.data?.error || 'Failed to load team matchup matrix.')
      } finally {
        setLoading(false)
      }
    }

    loadGames()
  }, [])

  const { matrix, maxCount, totalTrackedGames } = useMemo(() => {
    const counts = createEmptyMatrix()
    let trackedGames = 0

    for (const game of games) {
      const homeTeam = normalizePlayableTeamAbbreviation(game.homeTeam)
      const awayTeam = normalizePlayableTeamAbbreviation(game.awayTeam)

      if (!homeTeam || !awayTeam || homeTeam === awayTeam) {
        continue
      }

      counts[homeTeam][awayTeam] += 1
      counts[awayTeam][homeTeam] += 1
      trackedGames += 1
    }

    const highestCount = PLAYABLE_TEAM_ABBREVIATIONS.reduce((max, rowTeam) => {
      return Math.max(
        max,
        ...PLAYABLE_TEAM_ABBREVIATIONS
          .filter(columnTeam => columnTeam !== rowTeam)
          .map(columnTeam => counts[rowTeam][columnTeam])
      )
    }, 0)

    return {
      matrix: counts,
      maxCount: highestCount,
      totalTrackedGames: trackedGames,
    }
  }, [games])

  useEffect(() => {
    const tableWrap = tableWrapRef.current
    if (!tableWrap) {
      return
    }

    const centerMatrix = () => {
      const overflowWidth = tableWrap.scrollWidth - tableWrap.clientWidth
      if (overflowWidth > 0) {
        tableWrap.scrollLeft = overflowWidth / 2
      }
    }

    centerMatrix()
    window.addEventListener('resize', centerMatrix)

    return () => {
      window.removeEventListener('resize', centerMatrix)
    }
  }, [games])

  if (loading) {
    return <div className="team-matchup-matrix loading">Loading team matchup matrix...</div>
  }

  if (error) {
    return (
      <div className="team-matchup-matrix">
        <div className="error-message">{error}</div>
      </div>
    )
  }

  return (
    <div className="team-matchup-matrix">
      <header className="matrix-hero">
        <p className="matrix-kicker">League Balance Report</p>
        <h1>Team Matchup Matrix</h1>
        <p className="matrix-subtitle">
          Counts every completed game by team pairing. Hotter green cells have been played more often;
          red cells are the matchups still waiting for their prophecy.
        </p>
        <div className="matrix-summary">
          <span>{totalTrackedGames} tracked games</span>
          <span>Most repeated matchup: {maxCount}</span>
        </div>
      </header>

      <section className="matrix-card">
        <div className="matrix-legend" aria-label="Color legend">
          <span>Least played</span>
          <div className="matrix-legend-ramp" aria-hidden="true" />
          <span>Most played</span>
        </div>

        <div ref={tableWrapRef} className="matrix-table-wrap">
          <table className="matrix-table">
            <thead>
              <tr>
                <th className="matrix-corner" scope="col">
                  Teams
                </th>
                {PLAYABLE_TEAM_ABBREVIATIONS.map(team => (
                  <th key={`column-${team}`} scope="col" className="matrix-column-header">
                    <TeamAxisLabel team={team} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PLAYABLE_TEAM_ABBREVIATIONS.map(rowTeam => (
                <tr key={`row-${rowTeam}`}>
                  <th scope="row" className="matrix-row-header">
                    <TeamAxisLabel team={rowTeam} />
                  </th>
                  {PLAYABLE_TEAM_ABBREVIATIONS.map(columnTeam => {
                    const isSameTeam = rowTeam === columnTeam
                    const count = matrix[rowTeam][columnTeam]

                    return (
                      <td
                        key={`${rowTeam}-${columnTeam}`}
                        className={isSameTeam ? 'matrix-cell matrix-cell-self' : 'matrix-cell matrix-cell-clickable'}
                        style={isSameTeam ? undefined : getCellStyle(count, maxCount)}
                        title={isSameTeam ? `${rowTeam} vs ${columnTeam}` : `${rowTeam} vs ${columnTeam}: ${count}`}
                      >
                        {isSameTeam ? '' : (
                          <button
                            type="button"
                            className="matrix-cell-button"
                            onClick={() => navigate(`/matchups/${rowTeam}/${columnTeam}/games`)}
                            aria-label={`View ${rowTeam} versus ${columnTeam} game results`}
                          >
                            {count}
                          </button>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function createEmptyMatrix(): MatrixCounts {
  return Object.fromEntries(
    PLAYABLE_TEAM_ABBREVIATIONS.map(rowTeam => [
      rowTeam,
      Object.fromEntries(PLAYABLE_TEAM_ABBREVIATIONS.map(columnTeam => [columnTeam, 0])),
    ])
  ) as MatrixCounts
}

function getCellStyle(count: number, maxCount: number) {
  const ratio = maxCount > 0 ? count / maxCount : 0
  const hue = ratio * 122
  const saturation = 72
  const lightness = 28 + ratio * 17
  const alpha = 0.46 + ratio * 0.34

  return {
    backgroundColor: `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`,
    color: ratio > 0.45 ? '#fffdf1' : '#ffe8e8',
    boxShadow: count > 0 ? `inset 0 0 18px hsla(${hue}, 86%, 62%, 0.2)` : undefined,
  }
}

function TeamAxisLabel({ team }: { team: PlayableTeamAbbreviation }) {
  return (
    <span className="matrix-team-label">
      <TeamHelmet abbreviation={team} size="sm" className="matrix-team-helmet" />
      <span>{team}</span>
    </span>
  )
}
